import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGetIdentity } from "@refinedev/core";
import { Alert, Card, Select, Button, Space, Tag, Typography, Form, App } from "antd";
import type { AdminInfo } from "../../providers/authProvider";
import { FiltersCell } from "../../components/FiltersPreview";
import { ActiveSubscriptionSwitch } from "../../components/ActiveSubscriptionSwitch";
import { useSupabaseUsers } from "../../lib/useSupabaseUsers";
import { isEmptyFiltersDisplay } from "../../types/filters";
import type {
  CopyFilterRequest,
  CopyFilterResponse,
  UserFilterOption,
} from "../../types/filterCopy";

const { Title, Paragraph, Text } = Typography;

// Phrase ceiling enforced by the search engine (`MAX_SEARCH_PHRASES`): a query carrying more
// than this is rejected with TOO_MANY_PHRASES, so a copied filter above it would never run.
const MAX_SEARCH_PHRASES = 20;

// Polish plural of "fraza": 1 fraza, 2–4 frazy, 5+ fraz — with the 12–14 exception, which
// takes the "fraz" form despite ending in 2–4.
function phraseWord(count: number): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (count === 1) return "fraza";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "frazy";
  return "fraz";
}

/**
 * What the moderator is about to copy. The backend decodes `UserFilters.filter` into the same
 * `FiltersDisplay` the searches list uses, so the chips here mean exactly what they mean there.
 *
 * Beyond the chips it calls out the three states that make a copy go wrong silently:
 * an empty filter (matches everything), a pre-F5 filter still on the legacy `branches`
 * taxonomy, and a phrase list over the engine's cap.
 */
function FilterPreview({ filter }: { filter: UserFilterOption }) {
  const display = filter.filters_display;
  const phraseCount = display?.phrases.length ?? 0;

  return (
    <Card size="small" title="Zawartość filtra" style={{ marginBottom: 16 }}>
      <Space size={[4, 4]} wrap style={{ marginBottom: 12 }}>
        {filter.public ? <Tag color="gold">publiczny</Tag> : <Tag>prywatny</Tag>}
        {phraseCount > 1 && (
          <Tag color="magenta">
            {phraseCount} {phraseWord(phraseCount)} — dowolna z nich (LUB)
          </Tag>
        )}
      </Space>

      {display === null || display === undefined ? (
        <Text type="secondary">Nie udało się odczytać zawartości filtra.</Text>
      ) : (
        // Truncated on purpose, not `full`: a real filter carries up to 167 CPV codes, which
        // would bury the copy form. The overflow chip's tooltip holds the rest.
        <FiltersCell filters={display} />
      )}

      {display && isEmptyFiltersDisplay(display) && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 12 }}
          message="Filtr nie zawęża niczego"
          description="Ten filtr nie ma ani frazy, ani żadnego kryterium — po skopiowaniu będzie pasował do wszystkich ogłoszeń."
        />
      )}

      {display && display.branches.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 12 }}
          message="Stara taksonomia „branches”"
          description="Filtr nadal używa pola branches[] zamiast cpv_code[] (sprzed migracji F5). Skopiuje się wiernie, ale będzie filtrował po starej taksonomii, nie po kodach CPV."
        />
      )}

      {phraseCount > MAX_SEARCH_PHRASES && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 12 }}
          message="Za dużo fraz"
          description={`Filtr ma ${phraseCount} ${phraseWord(phraseCount)}, a wyszukiwarka przyjmuje maksymalnie ${MAX_SEARCH_PHRASES} (TOO_MANY_PHRASES). Kopia będzie zwracać błąd zamiast wyników.`}
        />
      )}
    </Card>
  );
}

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

function adminHeaders(): Record<string, string> {
  const token = localStorage.getItem("access_token");
  const h: Record<string, string> = { "Content-Type": "application/json" };
  if (token) h["Authorization"] = `Bearer ${token}`;
  return h;
}

async function adminFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: { ...adminHeaders(), ...init?.headers },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Request failed: ${response.statusText}`);
  }
  return response.json() as Promise<T>;
}

export const FilterCopyList = () => {
  const { notification } = App.useApp();
  const { data: identity } = useGetIdentity<AdminInfo>();
  const sourceDefaultApplied = useRef(false);
  const [sourceUserId, setSourceUserId] = useState<string | undefined>();
  const [filterId, setFilterId] = useState<string | undefined>();
  const [targetUserId, setTargetUserId] = useState<string | undefined>();
  const [copying, setCopying] = useState(false);

  const [activeOnly, setActiveOnly] = useState(true);
  const { users, loading: usersLoading } = useSupabaseUsers(activeOnly, [sourceUserId, targetUserId]);
  const [filters, setFilters] = useState<UserFilterOption[]>([]);
  const [filtersLoading, setFiltersLoading] = useState(false);

  useEffect(() => {
    if (sourceDefaultApplied.current || usersLoading || !identity?.id) {
      return;
    }
    // Domyślne źródło (własne konto) tylko względem pierwszej wczytanej listy — późniejsze
    // przełączenie filtra subskrypcji nie może nadpisać źródła wybranego ręcznie.
    sourceDefaultApplied.current = true;
    if (users.some((u) => u.id === identity.id)) {
      setSourceUserId(identity.id);
    }
  }, [identity?.id, users, usersLoading]);

  const loadFilters = useCallback(
    async (userId: string) => {
      setFiltersLoading(true);
      try {
        const response = await adminFetch<{ data: UserFilterOption[] }>(
          `${API_URL}/api/v1/admin/supabase/users/${encodeURIComponent(userId)}/user-filters`,
        );
        setFilters(response.data ?? []);
      } catch (error) {
        setFilters([]);
        notification.error({
          message: "Nie udało się pobrać filtrów",
          description: error instanceof Error ? error.message : "Wystąpił nieoczekiwany błąd.",
        });
      } finally {
        setFiltersLoading(false);
      }
    },
    [notification],
  );

  useEffect(() => {
    setFilterId(undefined);
    if (sourceUserId) {
      loadFilters(sourceUserId);
    } else {
      setFilters([]);
    }
  }, [sourceUserId, loadFilters]);

  const targetUserOptions = useMemo(
    () => users.filter((u) => u.id !== sourceUserId),
    [users, sourceUserId],
  );

  const selectedFilter = useMemo(
    () => filters.find((f) => f.id === filterId),
    [filters, filterId],
  );

  const canCopy = Boolean(sourceUserId && filterId && targetUserId);

  const handleCopy = async () => {
    if (!sourceUserId || !filterId || !targetUserId) return;

    setCopying(true);
    try {
      const payload: CopyFilterRequest = {
        source_user_id: sourceUserId,
        filter_id: filterId,
        target_user_id: targetUserId,
      };
      const data = await adminFetch<CopyFilterResponse>(`${API_URL}/api/v1/admin/filters/copy`, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      const copiedName = data?.data?.name ?? filters.find((f) => f.id === filterId)?.name ?? "";

      // Name what came across, so the moderator can tell a filter that notifies from one that
      // is merely saved — the difference is invisible in the filters list.
      const extras: string[] = [];
      if (data?.subscriptions_copied) extras.push("powiadomienia (kanały i częstotliwość)");
      if (data?.preferences_copied) extras.push("preferencję filtra");
      if (data?.should_send_copied) extras.push(`harmonogram raportów (${data.should_send_copied})`);
      if (data?.shares_copied) extras.push(`udostępnienia (${data.shares_copied})`);
      const extrasInfo = extras.length
        ? ` Przeniesiono również: ${extras.join(", ")}.`
        : " Filtr źródłowy nie miał żadnych powiązanych ustawień.";

      notification.success({
        message: "Filtr skopiowany",
        description: copiedName
          ? `Skopiowano filtr „${copiedName}”.${extrasInfo}`
          : `Filtr został skopiowany pomyślnie.${extrasInfo}`,
      });
      setFilterId(undefined);
      setTargetUserId(undefined);
    } catch (error) {
      notification.error({
        message: "Nie udało się skopiować filtra",
        description: error instanceof Error ? error.message : "Wystąpił nieoczekiwany błąd.",
      });
    } finally {
      setCopying(false);
    }
  };

  return (
    <Card>
      <Title level={4}>Kopiowanie filtru</Title>
      <Paragraph type="secondary">
        Wybierz użytkownika źródłowego, jego filtr oraz użytkownika docelowego. Kopiowana jest
        nazwa, dane filtra i flaga publiczności, a wraz z nimi wszystkie powiązane ustawienia:
        powiadomienia (kanały e-mail/SMS i częstotliwość), preferencja filtra, harmonogram
        raportów oraz udostępnienia. Po wybraniu filtra zobaczysz jego zawartość — branże, kody
        CPV i pozostałe kryteria — zanim go skopiujesz.
      </Paragraph>

      <Form layout="vertical" style={{ maxWidth: 480 }}>
        <Form.Item>
          <ActiveSubscriptionSwitch checked={activeOnly} onChange={setActiveOnly} />
        </Form.Item>
        <Form.Item label="Użytkownik źródłowy" required>
          <Select
            showSearch
            placeholder="Wybierz użytkownika"
            loading={usersLoading}
            value={sourceUserId}
            onChange={setSourceUserId}
            optionFilterProp="label"
            options={users.map((u) => ({
              value: u.id,
              label: u.email || u.id,
            }))}
            allowClear
          />
        </Form.Item>

        <Form.Item label="Filtr" required>
          <Select
            showSearch
            placeholder={sourceUserId ? "Wybierz filtr" : "Najpierw wybierz użytkownika źródłowego"}
            loading={filtersLoading}
            disabled={!sourceUserId}
            value={filterId}
            onChange={setFilterId}
            optionFilterProp="label"
            options={filters.map((f) => ({
              value: f.id,
              label: f.name || f.id,
            }))}
            allowClear
          />
        </Form.Item>

        {selectedFilter && <FilterPreview filter={selectedFilter} />}

        <Form.Item label="Użytkownik docelowy" required>
          <Select
            showSearch
            placeholder="Wybierz użytkownika docelowego"
            loading={usersLoading}
            disabled={!sourceUserId}
            value={targetUserId}
            onChange={setTargetUserId}
            optionFilterProp="label"
            options={targetUserOptions.map((u) => ({
              value: u.id,
              label: u.email || u.id,
            }))}
            allowClear
          />
        </Form.Item>

        <Space>
          <Button type="primary" onClick={handleCopy} loading={copying} disabled={!canCopy}>
            Kopiuj filtr
          </Button>
        </Space>
      </Form>
    </Card>
  );
};
